import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Trophy, Lock, CheckCircle, Play, Clock, Package,
  Loader2, Zap, X, Sparkles, Award
} from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';

const BadgesPage = () => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [startingBadge, setStartingBadge] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catalogRes, progressRes] = await Promise.all([
        apiClient.request('/api/badges/catalog'),
        apiClient.request('/api/badges/my-progress')
      ]);
      setCatalog(catalogRes);
      setUserProgress(progressRes);
    } catch (err) {
      console.error('Failed to fetch badge data:', err);
    } finally {
      setLoading(false);
    }
  };

  const startBadge = async (badgeId) => {
    setStartingBadge(badgeId);
    try {
      await apiClient.request(`/api/badges/start/${badgeId}`, { method: 'POST' });
      await fetchData();
    } catch (err) {
      console.error('Failed to start badge:', err);
    } finally {
      setStartingBadge(null);
    }
  };

  const getBadgeProgress = (badgeId) => {
    return userProgress?.badges?.find(b => b.id === badgeId) || null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': case 'verified': return 'bg-green-500';
      case 'in_progress': return 'bg-blue-500';
      case 'pending_verification': return 'bg-yellow-500';
      case 'ordered': case 'shipped': return 'bg-purple-500';
      default: return 'bg-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'verified': return 'Verified';
      case 'in_progress': return 'In Progress';
      case 'pending_verification': return 'Pending Review';
      case 'ordered': return 'Badge Ordered';
      case 'shipped': return 'Badge Shipped';
      default: return 'Locked';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="badges-loading">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </DashboardLayout>
    );
  }

  const totalCompleted = userProgress?.total_completed || 0;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="badges-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Celebrations</h1>
              <p className="text-gray-600 mt-1">Complete real-world challenges to earn badges</p>
            </div>
            <button
              onClick={() => navigate('/badges/orders')}
              data-testid="my-orders-btn"
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
            >
              <Package size={16} /> My Orders
            </button>
          </div>

          {/* Progress Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="p-4 bg-gradient-to-br from-purple-600 to-pink-500 rounded-xl text-white">
              <Trophy size={22} className="mb-1.5 opacity-80" />
              <p className="text-2xl font-bold">{totalCompleted}/20</p>
              <p className="text-xs opacity-80">Total Earned</p>
            </div>
            {catalog?.levels?.map((level) => {
              const lp = userProgress?.level_progress?.[level.level] || { completed: 0, total: 5 };
              return (
                <div key={level.level} className="p-4 bg-white rounded-xl border border-gray-200">
                  <span className="text-xl">{level.icon}</span>
                  <p className="text-lg font-bold text-gray-900 mt-1">{lp.completed}/{lp.total}</p>
                  <p className="text-xs text-gray-500">{level.subtitle}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming Badges Guide (shown when few or no badges earned) */}
        {totalCompleted < 3 && (
          <div className="mb-8 p-6 bg-gradient-to-br from-purple-50 to-orange-50 rounded-2xl border border-purple-100" data-testid="upcoming-badges-guide">
            <h2 className="text-lg font-bold text-gray-900 mb-2">How to Earn Celebrations</h2>
            <p className="text-sm text-gray-600 mb-5">Complete real-world actions to unlock badges. Here are your next milestones:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { icon: '💬', name: 'First Real Conversation', desc: 'Log your first real-world chat', action: 'Log a conversation in Real-World Challenges' },
                { icon: '🔥', name: '7-Day Challenger', desc: 'Complete a challenge every day for a week', action: 'Keep your real-world streak going for 7 days' },
                { icon: '🦅', name: 'Going Solo', desc: '10 real-world wins without a practice session', action: 'Prove you can do it on your own' },
                { icon: '🤝', name: 'Connection Maker', desc: 'Make plans with a real person', action: 'Log "Made plans with someone" in challenges' },
                { icon: '🌟', name: 'Week Without AvA', desc: 'A full week of real-world action, no practice needed', action: 'The ultimate sign of growth' },
                { icon: '🎓', name: 'Ready for the Real World', desc: 'Social Score 80+ with 50 real-world actions', action: 'You may not need HiBFF anymore!' },
              ].map((badge, i) => (
                <div key={i} className="p-4 bg-white rounded-xl border border-gray-200 flex items-start gap-3">
                  <span className="text-2xl">{badge.icon}</span>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{badge.name}</p>
                    <p className="text-xs text-gray-500 mb-1">{badge.desc}</p>
                    <p className="text-xs text-purple-600 font-medium">{badge.action}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Badge Levels */}
        <div className="space-y-8">
          {catalog?.levels?.map((level, levelIndex) => {
            const lp = userProgress?.level_progress?.[level.level] || { completed: 0, total: 5, unlocked: levelIndex === 0 };
            const isUnlocked = lp.unlocked;

            return (
              <div key={level.level} className="relative" data-testid={`badge-level-${level.level}`}>
                {/* Level Header */}
                <div
                  className="p-4 rounded-t-xl"
                  style={{ background: isUnlocked ? `linear-gradient(to right, ${level.color}, ${level.color}dd)` : '#9CA3AF' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{level.icon}</span>
                      <div>
                        <h2 className="text-lg font-bold text-white">Level {levelIndex + 1}: {level.name}</h2>
                        <p className="text-white/80 text-xs">{level.subtitle}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isUnlocked && (
                        <span className="flex items-center gap-1 px-3 py-1 bg-white/20 rounded-full text-white text-xs">
                          <Lock size={12} /> Locked
                        </span>
                      )}
                      <span className="px-3 py-1 bg-white/20 rounded-full text-white text-xs font-semibold">{lp.completed}/{lp.total}</span>
                    </div>
                  </div>
                  <p className="text-white/70 text-xs mt-2">{level.description}</p>
                  {!isUnlocked && level.unlock_requirement && (
                    <p className="text-white/60 text-xs mt-1">Complete {level.unlock_requirement} badges from previous level to unlock</p>
                  )}
                </div>

                {/* Badges Grid */}
                <div className={`p-4 bg-white rounded-b-xl border border-t-0 border-gray-200 ${!isUnlocked ? 'opacity-50' : ''}`}>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {level.badges?.map((badge) => {
                      const progress = getBadgeProgress(badge.id);
                      const status = progress?.status || 'locked';
                      const isCompleted = ['completed', 'verified', 'ordered', 'shipped', 'delivered'].includes(status);
                      const isInProgress = status === 'in_progress';
                      // Sprint 207 — Challenge 10 ("The Slow Observer") is the pivotal NFT moment.
                      // Anything sequence >= 10 mints a Crossmint NFT to the teen's custodial vault.
                      const isNftTier = (badge.number || 0) >= 10;
                      const isPivotal = badge.number === 10;

                      return (
                        <div
                          key={badge.id}
                          onClick={() => isUnlocked && setSelectedBadge(badge)}
                          data-testid={`badge-card-${badge.id}`}
                          className={`relative p-3.5 rounded-xl border-2 transition-all cursor-pointer ${
                            isPivotal && isUnlocked ? 'border-amber-400 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 hover:shadow-xl badge-pivot-glow'
                            : isCompleted ? 'border-green-300 bg-green-50 hover:shadow-md'
                            : isInProgress ? 'border-blue-300 bg-blue-50 hover:shadow-md'
                            : isUnlocked ? 'border-gray-200 hover:border-purple-300 hover:shadow-md'
                            : 'border-gray-200 cursor-not-allowed'
                          }`}
                        >
                          <div className="absolute -top-2 -left-2 w-5 h-5 bg-gray-800 rounded-full flex items-center justify-center text-white text-[10px] font-bold">{badge.number}</div>
                          {status !== 'locked' && (
                            <div className={`absolute -top-2 -right-2 w-5 h-5 ${getStatusColor(status)} rounded-full flex items-center justify-center`}>
                              {isCompleted ? <CheckCircle size={12} className="text-white" /> : <Play size={10} className="text-white" />}
                            </div>
                          )}
                          {isPivotal && (
                            <div
                              className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider text-white shadow-md"
                              style={{ background: 'linear-gradient(90deg, #F59E0B 0%, #EF4444 100%)' }}
                              data-testid="badge-nft-pivot-banner"
                            >
                              1st NFT
                            </div>
                          )}
                          <div className="text-center mb-2">
                            <span className={`text-3xl ${!isUnlocked ? 'grayscale' : ''}`}>{badge.icon}</span>
                          </div>
                          <h3 className="font-semibold text-gray-900 text-xs text-center mb-1.5 line-clamp-2 leading-tight">{badge.name}</h3>
                          {isInProgress && progress?.progress_target > 1 && (
                            <div className="mt-1.5">
                              <div className="flex justify-between text-[10px] text-gray-500 mb-0.5">
                                <span>Progress</span>
                                <span>{progress.progress}/{progress.progress_target}</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1">
                                <div className="bg-blue-500 h-1 rounded-full" style={{ width: `${(progress.progress / progress.progress_target) * 100}%` }} />
                              </div>
                            </div>
                          )}
                          <p className={`text-[10px] text-center mt-1.5 font-medium ${isCompleted ? 'text-green-600' : isInProgress ? 'text-blue-600' : isPivotal ? 'text-amber-700' : 'text-gray-400'}`}>
                            {isPivotal ? 'NFT Unlocks Here' : (isNftTier && isUnlocked ? 'NFT Tier' : getStatusLabel(status))}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Badge Detail Modal */}
        {selectedBadge && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedBadge(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="badge-detail-modal">
              <div className="p-6">
                <div className="flex justify-end mb-2">
                  <button onClick={() => setSelectedBadge(null)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
                </div>
                <div className="text-center mb-6">
                  <span className="text-5xl mb-3 block">{selectedBadge.icon}</span>
                  <h2 className="text-xl font-bold text-gray-900">{selectedBadge.name}</h2>
                  <p className="text-gray-600 text-sm mt-1">{selectedBadge.description}</p>
                  <span className={`inline-block mt-3 px-3 py-1 rounded-full text-xs font-semibold ${
                    selectedBadge.rarity === 'legendary' ? 'bg-yellow-100 text-yellow-700' :
                    selectedBadge.rarity === 'epic' ? 'bg-purple-100 text-purple-700' :
                    selectedBadge.rarity === 'rare' ? 'bg-blue-100 text-blue-700' :
                    selectedBadge.rarity === 'uncommon' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedBadge.rarity?.charAt(0).toUpperCase() + selectedBadge.rarity?.slice(1)}
                  </span>
                </div>

                <div className="mb-5 p-4 bg-gray-50 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-1.5 text-sm">How to Earn</h3>
                  <p className="text-gray-700 text-sm">{selectedBadge.requirement}</p>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div className="p-3 bg-red-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-red-600">{selectedBadge.hearts_reward}</p>
                    <p className="text-xs text-gray-600">Hearts</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-purple-600">{selectedBadge.xp_reward} XP</p>
                    <p className="text-xs text-gray-600">Experience</p>
                  </div>
                </div>

                {/* Sprint 207 — virtual badges + NFT. NO PHYSICAL BADGES. */}
                {(() => {
                  const seq = selectedBadge.number || selectedBadge.sequence || 0;
                  const isNftTier = seq >= 10;
                  const isPivotal = seq === 10;
                  if (isPivotal) {
                    return (
                      <div
                        className="mb-5 p-4 rounded-xl border-2 flex items-start gap-3"
                        style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FEF2F2 100%)', borderColor: '#F59E0B' }}
                        data-testid="badge-pivotal-nft-banner"
                      >
                        <Sparkles size={22} className="text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="font-bold text-amber-900 text-sm">Pivotal milestone — your 1st NFT</h3>
                          <p className="text-xs text-amber-800 mt-0.5">
                            Completing this challenge mints your <strong>first custom NFT</strong> — uniquely generated from your BIG5/OCEAN personality and Maya Likes, secured in your HiBFF Vault.
                          </p>
                        </div>
                      </div>
                    );
                  }
                  if (isNftTier) {
                    return (
                      <div
                        className="mb-5 p-3 rounded-xl border flex items-start gap-3"
                        style={{ background: '#F5F3FF', borderColor: '#C4B5FD' }}
                        data-testid="badge-nft-tier-banner"
                      >
                        <Sparkles size={20} className="text-purple-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="font-semibold text-purple-900 text-sm">Custom NFT reward</h3>
                          <p className="text-xs text-purple-700 mt-0.5">Mints a personality-personalised NFT into your HiBFF Vault on completion.</p>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div
                      className="mb-5 p-3 rounded-xl border flex items-start gap-3"
                      style={{ background: '#EFF6FF', borderColor: '#BFDBFE' }}
                      data-testid="badge-virtual-banner"
                    >
                      <Award size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-blue-900 text-sm">Virtual badge + trophy card</h3>
                        <p className="text-xs text-blue-700 mt-0.5">Auto-generates a one-of-a-kind trophy card celebrating your win — saved to your Vault.</p>
                      </div>
                    </div>
                  );
                })()}

                <div className="flex gap-3">
                  <button onClick={() => setSelectedBadge(null)} className="flex-1 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 text-sm" data-testid="badge-modal-close-btn">Close</button>
                  {(() => {
                    const progress = getBadgeProgress(selectedBadge.id);
                    const status = progress?.status || 'locked';
                    if (status === 'locked') {
                      return (
                        <button
                          onClick={() => startBadge(selectedBadge.id)}
                          disabled={startingBadge === selectedBadge.id}
                          data-testid="start-badge-btn"
                          className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                        >
                          {startingBadge === selectedBadge.id ? <Loader2 size={18} className="animate-spin" /> : <><Zap size={18} /> Start Challenge</>}
                        </button>
                      );
                    }
                    if (status === 'in_progress') {
                      return (
                        <button className="flex-1 py-3 bg-blue-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 text-sm" data-testid="badge-modal-continue-btn">
                          <Play size={18} /> Continue
                        </button>
                      );
                    }
                    return (
                      <button
                        disabled
                        className="flex-1 py-3 bg-green-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 text-sm"
                        data-testid="badge-modal-completed-btn"
                      >
                        <CheckCircle size={18} /> Completed!
                      </button>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BadgesPage;
